import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

/**
 * useCanvasActivity - Smart polling that only runs when there's active work
 * Stops polling 60 seconds after last activity
 */
const useCanvasActivity = (canvasId, onActivityComplete) => {
    const [hasActiveWork, setHasActiveWork] = useState(false);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPolling, setIsPolling] = useState(false);

    const seenCompletionIds = useRef(new Set());
    const lastCheckTime = useRef(Date.now());
    const lastActivityTime = useRef(0);

    const checkActivity = useCallback(async () => {
        if (!canvasId) return false;

        try {
            const since = lastCheckTime.current;
            lastCheckTime.current = Date.now();

            const response = await api.get(`/canvas-activity/${canvasId}/active?since=${since}`);
            const { activities: activeList, hasActive, recentlyCompleted } = response.data;

            setActivities(activeList || []);
            setHasActiveWork(hasActive);

            // Detect new completions
            if (recentlyCompleted && recentlyCompleted.length > 0) {
                const newCompletions = recentlyCompleted.filter(a => !seenCompletionIds.current.has(a.id));

                if (newCompletions.length > 0) {
                    console.log(`[CanvasActivity] Detected ${newCompletions.length} new completions, triggering refresh`);
                    newCompletions.forEach(a => seenCompletionIds.current.add(a.id));
                    lastActivityTime.current = Date.now();
                    onActivityComplete?.();
                }
            }

            // Update activity time if work is happening
            if (hasActive) {
                lastActivityTime.current = Date.now();
                setIsPolling(true);
            }

            // Stop polling if no activity for 60 seconds
            const timeSinceActivity = Date.now() - lastActivityTime.current;
            if (timeSinceActivity > 60000 && !hasActive) {
                console.log('[CanvasActivity] No activity for 60s, stopping polling');
                setIsPolling(false);
            }

            return hasActive;
        } catch (error) {
            console.error('[CanvasActivity] Error checking activity:', error);
            return false;
        }
    }, [canvasId, onActivityComplete]);

    // Initial check on mount
    useEffect(() => {
        if (canvasId) {
            setLoading(true);
            seenCompletionIds.current = new Set();
            lastCheckTime.current = Date.now() - 5000;
            checkActivity().finally(() => setLoading(false));
        }
    }, [canvasId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll only when active work detected
    useEffect(() => {
        if (!canvasId || !isPolling) return;

        console.log('[CanvasActivity] Polling started');
        const interval = setInterval(checkActivity, 5000);
        return () => {
            console.log('[CanvasActivity] Polling stopped');
            clearInterval(interval);
        };
    }, [canvasId, isPolling, checkActivity]);

    const refresh = useCallback(() => checkActivity(), [checkActivity]);

    return { hasActiveWork, activities, loading, refresh, isPolling };
};

export default useCanvasActivity;
